/* Collection page controls: the right-side filter drawer (open/close, same
   pattern as cart-drawer/search-drawer), the sort dropdown, and the filter
   form itself (checkboxes / price range / clear link / pagination links).

   All of these used to be real GET navigations -- correct, but every single
   checkbox click reloaded the whole page. They're now intercepted and
   turned into a Section Rendering API fetch instead (same technique
   search-drawer.js already uses for live search results): re-request this
   collection URL with "&section_id=<the running section's id>", pull the
   fresh "#CollectionTop-<id>" / "#CollectionGrid-<id>" / "#CollectionFilters-
   <id>" markup out of that response, and swap each into the live page. The
   <form> is still a plain working GET form underneath, so this all still
   degrades to a normal page reload with JavaScript disabled or if a fetch
   ever fails. */
(function () {
  class CollectionFilterDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('#CollectionFilterDrawer-Overlay');
      this.closeButton = this.querySelector('.drawer__close');
      this.openedBy = null;

      this.overlay && this.overlay.addEventListener('click', this.close.bind(this));
      this.closeButton && this.closeButton.addEventListener('click', this.close.bind(this));
      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });
    }

    open(triggeredBy) {
      this.openedBy = triggeredBy || null;
      setTimeout(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      const inner = this.querySelector('.drawer__inner');
      if (inner && typeof trapFocus === 'function') trapFocus(inner);
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
      document.body.classList.remove('overflow-hidden');
    }
  }

  if (!customElements.get('collection-filter-drawer')) {
    customElements.define('collection-filter-drawer', CollectionFilterDrawer);
  }

  function getSectionId() {
    const root = document.querySelector('[data-section-id]');
    return root ? root.dataset.sectionId : null;
  }

  // Guards against a fast double-click (or a checkbox toggled again before
  // the first request lands) applying an out-of-order/stale response --
  // same "only the newest request wins" pattern as search-drawer.js.
  let requestId = 0;

  /* Fetches this collection (with whatever query string "url" carries) as
     just this one section's markup, and swaps the three live regions that
     can change (top controls/count, the grid+pagination, the filter drawer
     contents) for their freshly-rendered equivalents. Falls back to a real
     navigation if the section id can't be found or the request fails, so a
     broken fetch never leaves the user stuck. */
  function refreshCollection(url, pushState) {
    const sectionId = getSectionId();
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    const topEl = document.getElementById('CollectionTop-' + sectionId);
    const gridEl = document.getElementById('CollectionGrid-' + sectionId);
    const filtersEl = document.getElementById('CollectionFilters-' + sectionId);

    const separator = url.indexOf('?') === -1 ? '?' : '&';
    const fetchUrl = url + separator + 'section_id=' + encodeURIComponent(sectionId);

    const thisRequest = ++requestId;
    if (gridEl) gridEl.classList.add('is-loading');

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Collection filter request failed');
        return res.text();
      })
      .then((html) => {
        if (thisRequest !== requestId) return; // a newer filter change already superseded this request

        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (topEl) {
          const freshTop = doc.getElementById('CollectionTop-' + sectionId);
          if (freshTop) topEl.innerHTML = freshTop.innerHTML;
        }
        if (gridEl) {
          const freshGrid = doc.getElementById('CollectionGrid-' + sectionId);
          if (freshGrid) gridEl.innerHTML = freshGrid.innerHTML;
        }
        if (filtersEl) {
          const freshFilters = doc.getElementById('CollectionFilters-' + sectionId);
          if (freshFilters) filtersEl.innerHTML = freshFilters.innerHTML;
        }

        if (pushState) window.history.pushState({ collectionAjax: true }, '', url);

        // The top controls (filter toggle + sort select) just got replaced
        // wholesale, so their click/change listeners need re-binding --
        // ".dataset.filterBound"/".dataset.sortBound" reset to falsy on the
        // fresh nodes automatically, so this is a no-op for anything that
        // wasn't actually replaced.
        initFilterToggle();
        initSortSelect();

        // Fresh product cards in the grid need the same re-init any other
        // raw-HTML swap in this theme does (see search-drawer.js).
        if (typeof window.initProductCardSwipers === 'function') window.initProductCardSwipers();
        if (typeof window.initQuickViewTriggers === 'function') window.initQuickViewTriggers();

        // Only relevant once results have scrolled out of view (e.g. after
        // paginating) -- checking a filter box while the grid is already
        // on-screen shouldn't yank the page around.
        const rect = (topEl || gridEl) ? (topEl || gridEl).getBoundingClientRect() : null;
        if (rect && rect.top < 0) {
          (topEl || gridEl).scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(() => {
        if (thisRequest === requestId) window.location.href = url;
      })
      .finally(() => {
        if (thisRequest === requestId && gridEl) gridEl.classList.remove('is-loading');
      });
  }

  function initFilterToggle() {
    document.querySelectorAll('[data-filter-toggle]').forEach((trigger) => {
      if (trigger.dataset.filterBound) return;
      trigger.dataset.filterBound = 'true';
      trigger.addEventListener('click', () => {
        const drawer = document.querySelector('collection-filter-drawer');
        if (drawer) drawer.open(trigger);
      });
    });
  }

  function initSortSelect() {
    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      if (select.dataset.sortBound) return;
      select.dataset.sortBound = 'true';
      select.addEventListener('change', () => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', select.value);
        refreshCollection(window.location.pathname + '?' + params.toString(), true);
      });
    });
  }

  /* Bound once, directly on the <form> element -- it's never replaced by
     the AJAX swap above (only its "#CollectionFilters-<id>" contents are),
     so these listeners keep working across every filter change with no
     re-binding needed, via plain event delegation. */
  function initFilterForm() {
    const form = document.querySelector('.collection-filter-drawer__form');
    if (!form || form.dataset.filterFormBound) return;
    form.dataset.filterFormBound = 'true';

    function submitViaAjax() {
      const params = new URLSearchParams(new FormData(form));
      const action = form.getAttribute('action') || window.location.pathname;
      refreshCollection(action + '?' + params.toString(), true);
    }

    form.addEventListener('submit', (evt) => {
      evt.preventDefault();
      submitViaAjax();
    });

    form.addEventListener('change', (evt) => {
      if (evt.target.matches('.collection-filter-drawer__checkbox, .collection-filter-drawer__price-input')) {
        submitViaAjax();
      }
    });

    form.addEventListener('click', (evt) => {
      const clearLink = evt.target.closest('.collection-filter-drawer__clear');
      if (clearLink) {
        evt.preventDefault();
        refreshCollection(clearLink.getAttribute('href'), true);
      }
    });
  }

  /* Pagination links live inside "#CollectionGrid-<id>", which does get
     replaced wholesale on every refresh -- delegate from a stable ancestor
     (the section's own data-section-id root) instead of the grid wrapper
     itself so this only needs to be bound once, same as the form above. */
  function initPaginationLinks() {
    const root = document.querySelector('[data-section-id]');
    if (!root || root.dataset.paginationBound) return;
    root.dataset.paginationBound = 'true';

    root.addEventListener('click', (evt) => {
      const link = evt.target.closest('.collection__pagination-link[href]');
      if (!link) return;
      evt.preventDefault();
      refreshCollection(link.getAttribute('href'), true);
    });
  }

  function init() {
    initFilterToggle();
    initSortSelect();
    initFilterForm();
    initPaginationLinks();
  }

  // Back/forward should reflect the URL Shopify actually navigated to
  // (pushState above only ever stores the collection URL itself), so a
  // plain re-fetch of the current location without another pushState call
  // keeps history navigation in sync with the grid on screen.
  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-section-id]')) {
      refreshCollection(window.location.href, false);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
